/**
 * Memory stage definitions (data). Each stage is a small "theatre" scene launched over the
 * prologue (PrologueStageScene); the controllers in ./controllers.ts read these to build the set,
 * place the cast and time the beats. Tints follow ART_DIRECTION "Memory stages".
 *
 * Flow per stage: base MemoryStage plays `intro`; the controller then runs the interactive part
 * (balance / roam / gaze / scripted) and finally plays `main` (or the main interaction's timeline).
 */
import type { Facing } from '@/core/types';
import type { MemoryStageId } from '../letter';
import type { PrologueActorId } from '../actors';

export interface StagePlacement { id: PrologueActorId; x: number; y: number; facing: Facing }

export interface StageInteraction {
  id: string;
  x: number; y: number; w: number; h: number;
  kind: 'talk' | 'observe' | 'trigger';
  /** Actor to face for 'talk'. */
  actor?: PrologueActorId;
  dialogue?: string;
  timeline?: string;
  /** Second timeline chained after `timeline` (e.g. the next day). */
  then?: string;
  label: string;
  /** Fires on entering the rect. */
  auto?: boolean;
  /** Main-line trigger: the stage completes after it (fires once per stage run). */
  main?: boolean;
}

export interface StageCaption {
  /** Seconds from the start of the interactive phase. */
  at: number;
  text: string;
}

export type StageKind = 'storm' | 'roam' | 'gaze' | 'scripted';

export interface StageDef {
  id: MemoryStageId;
  kind: StageKind;
  title: string;
  /** World size in pixels. */
  width: number; height: number;
  /** Background colour and the semi-transparent tint plate over the set. */
  bg: number;
  tint: number; tintAlpha: number;
  /** Timeline the base MemoryStage plays first. */
  intro: string;
  /** Timeline the controller plays after the interactive phase (roam stages use the main interaction instead). */
  main?: string;
  /** Where the player starts. */
  player: StagePlacement;
  cast: StagePlacement[];
  interactions: StageInteraction[];
  /** Collision sketch (rows of chars; '.' and 'P' walkable, everything else solid). Tile = 32px. */
  sketch: readonly string[];
  /** Ground palette for the dev renderer keyed by sketch char. */
  palette: Record<string, number>;
  /** Camera vignette (radius 0..1, strength 0..1); softer than the cinematics default. */
  vignette?: { radius: number; strength: number; color: number };
  /** Guidance shown at the top for a few seconds (中文). */
  hint?: string;
  music?: string;
  ambience?: string[];
}

const P = (id: PrologueActorId, tx: number, ty: number, facing: Facing): StagePlacement => ({ id, x: tx * 32 + 16, y: ty * 32 + 28, facing });

/** MEMORY_STORM — the pitching deck at night. Semi-controllable: ← → keeps footing for ~20 s. */
export const STORM_STAGE: StageDef = {
  id: 'memory-storm',
  kind: 'storm',
  title: '黑夜·风浪',
  width: 640, height: 384,
  bg: 0x0a0d1a, tint: 0x1a2140, tintAlpha: 0.22,
  intro: 'prologue-stage-storm-intro',
  main: 'prologue-stage-storm-dawn',
  player: P('jiang', 9, 7, 'up'),
  cast: [P('sailor-1', 6, 5, 'up'), P('sailor-2', 13, 5, 'up'), P('sailor-3', 11, 8, 'left')],
  interactions: [],
  sketch: [
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~..............~~',
    '~~~..............~~~',
    '~~~..............~~~',
    '~~~..............~~~',
    '~~~~..............~~',
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
  ],
  palette: { '~': 0x12224a, '.': 0x6a5238 },
  vignette: { radius: 0.78, strength: 0.55, color: 0x000000 },
  hint: '← → 在颠簸的甲板上站稳',
  music: 'bgm-memory-storm',
  ambience: ['amb-storm-rain', 'amb-storm-sea'],
};

/** Letter-text captions shown over the storm (each must be a substring of the letter). */
export const STORM_CAPTIONS: StageCaption[] = [
  { at: 1.5, text: '天啊，你要看见这样大的风浪一定为吓坏的，黑夜中闪电和雨水混在一起。' },
  { at: 11, text: '不过我是一个勇敢的男人，在颠簸的甲板上我仍然稳稳的站着，看着水手们控制着帆。' },
];
/** Sailor shouts (environmental flavour, allowed by STORY_ADAPTATION P2). */
export const STORM_SHOUTS = ['收帆！', '抓紧绳子！', '压住舵！', '又一个浪！', '别松手！'] as const;
/** Seconds of balancing before dawn. */
export const STORM_DURATION_S = 20;
/** Deck positions the sailors run between (tile coords). */
export const STORM_SAILOR_POSTS: Record<string, [number, number][]> = {
  'sailor-1': [[6, 5], [4, 6], [7, 4]],
  'sailor-2': [[13, 5], [15, 6], [12, 4]],
  'sailor-3': [[11, 8], [8, 8], [14, 7]],
};

/** MEMORY_ISLAND_RITUAL — grey dawn beach; controllable. */
export const ISLAND_STAGE: StageDef = {
  id: 'memory-island',
  kind: 'roam',
  title: '清晨·无名小岛',
  width: 640, height: 384,
  bg: 0x9aa4ad, tint: 0xb7c0c8, tintAlpha: 0.16,
  intro: 'prologue-stage-island-landing',
  player: P('jiang', 8, 5, 'down'),
  cast: [P('captain', 6, 5, 'down'), P('sailor-1', 8, 3, 'down'), P('sailor-2', 5, 3, 'down'), P('lubaoding', 15, 8, 'down')],
  interactions: [
    { id: 'island-captain', x: 5 * 32, y: 4 * 32, w: 3 * 32, h: 3 * 32, kind: 'talk', actor: 'captain', dialogue: 'prologue-island-captain', label: '和船长说话' },
    { id: 'island-rite', x: 12 * 32, y: 7 * 32, w: 5 * 32, h: 4 * 32, kind: 'trigger', timeline: 'prologue-stage-island-rite', label: '走近那个人', auto: true, main: true },
    { id: 'island-shore', x: 0, y: 9 * 32, w: 10 * 32, h: 2 * 32, kind: 'observe', dialogue: 'prologue-observe-island-shore', label: '看海' },
    { id: 'island-ship', x: 4 * 32, y: 1 * 32, w: 4 * 32, h: 2 * 32, kind: 'observe', dialogue: 'prologue-observe-island-ship', label: '看船' },
  ],
  sketch: [
    '~~~~~~~~~~~~~~~~~~~~',
    '~~~~####~~~~~~~~~~~~',
    '~~~~####~~~~~~~~~~~~',
    '~~~..........~~~~~~~',
    '~~..............~~~~',
    '~.................~~',
    '...................~',
    '....................',
    '....................',
    '~~~~................',
    '~~~~~~~.............',
    '~~~~~~~~~~..........',
  ],
  palette: { '~': 0x6f8894, '#': 0x4a3a2a, '.': 0xc9bfa6 },
  vignette: { radius: 0.8, strength: 0.35, color: 0x2a2f38 },
  hint: '方向键移动 · 走向沙滩上那个穿得破烂的人',
  music: 'bgm-memory-island',
  ambience: ['amb-island-waves', 'amb-island-gulls'],
};

/** MEMORY_BLACK_WATER — dim Xingtang sickroom; semi-controllable (hold confirm to gaze). */
export const BLACK_WATER_STAGE: StageDef = {
  id: 'memory-black-water',
  kind: 'gaze',
  title: '星塘·病榻',
  width: 640, height: 384,
  bg: 0x1a1108, tint: 0x5a3a18, tintAlpha: 0.18,
  intro: 'prologue-stage-water-intro',
  main: 'prologue-stage-water-vision',
  player: P('jiang', 12, 5, 'left'),
  cast: [P('wanqianmei', 9, 7, 'right'), P('father', 7, 6, 'right'), P('mother', 8, 6, 'right'), P('qiong', 6, 6, 'right')],
  interactions: [],
  sketch: [
    '####################',
    '####################',
    '##..............####',
    '##..............####',
    '##..............####',
    '##..............####',
    '##..............####',
    '##..............####',
    '##..............####',
    '##..............####',
    '####################',
    '####################',
  ],
  palette: { '#': 0x3a2612, '.': 0x7a5a38 },
  vignette: { radius: 0.72, strength: 0.6, color: 0x000000 },
  hint: '按住 确认键 盯着看',
  music: 'bgm-memory-sickroom',
  ambience: ['amb-winter-wind'],
};
/** Seconds the confirm key must be held before the black water appears. */
export const GAZE_HOLD_S = 3;
/** Where the triangle is drawn (world px). */
export const GAZE_TRIANGLE = { x: 336, y: 262 } as const;

/** MEMORY_FAREWELL — winter village; controllable. */
export const FAREWELL_STAGE: StageDef = {
  id: 'memory-farewell',
  kind: 'roam',
  title: '星塘·冬',
  width: 640, height: 384,
  bg: 0xdfe6ea, tint: 0xeef2f5, tintAlpha: 0.25,
  intro: 'prologue-stage-farewell-intro',
  player: P('jiang', 8, 8, 'up'),
  cast: [P('father', 9, 5, 'down'), P('qiong', 5, 7, 'right'), P('zhaosan', 15, 8, 'left'), P('villager', 12, 9, 'up'), P('zhao', 19, 8, 'left')],
  interactions: [
    { id: 'farewell-father', x: 8 * 32, y: 5 * 32, w: 3 * 32, h: 3 * 32, kind: 'trigger', timeline: 'prologue-stage-farewell-father', then: 'prologue-stage-farewell-zhao', label: '去找父亲', main: true },
    { id: 'farewell-qiong', x: 4 * 32, y: 6 * 32, w: 3 * 32, h: 3 * 32, kind: 'talk', actor: 'qiong', dialogue: 'prologue-farewell-qiong', label: '和琼说话' },
    { id: 'farewell-loom', x: 2 * 32, y: 4 * 32, w: 2 * 32, h: 2 * 32, kind: 'observe', dialogue: 'prologue-farewell-loom', label: '看织机' },
    { id: 'farewell-zhaosan', x: 14 * 32, y: 7 * 32, w: 3 * 32, h: 3 * 32, kind: 'talk', actor: 'zhaosan', dialogue: 'prologue-farewell-zhaosan', label: '和赵三说话' },
    { id: 'farewell-mountain', x: 12 * 32, y: 1 * 32, w: 8 * 32, h: 3 * 32, kind: 'observe', dialogue: 'prologue-farewell-mountain', label: '看山' },
    { id: 'farewell-well', x: 11 * 32, y: 8 * 32, w: 2 * 32, h: 2 * 32, kind: 'observe', dialogue: 'prologue-farewell-well', label: '看井' },
    { id: 'farewell-ice', x: 0, y: 10 * 32, w: 10 * 32, h: 2 * 32, kind: 'observe', dialogue: 'prologue-farewell-ice', label: '看冰面' },
  ],
  sketch: [
    '####################',
    '############MMMMMMMM',
    '#L#......###MMMMMMMM',
    '#L#......###MMMMMMMM',
    '#LL.................',
    '....................',
    '....................',
    '....................',
    '...........W........',
    '....................',
    '~~~~~~~~~~..........',
    '~~~~~~~~~~..........',
  ],
  palette: { '#': 0x6b5a4a, M: 0xb9c6d0, L: 0x8a5a32, W: 0x5a6a70, '.': 0xe8edf0, '~': 0xcfe0ea },
  vignette: { radius: 0.85, strength: 0.3, color: 0x5a6b7c },
  hint: '方向键移动 · 去找父亲；也可以看看村子',
  music: 'bgm-memory-farewell',
  ambience: ['amb-winter-wind'],
};

/** MEMORY_SWORD_TRAINING — dusty gold road with a wayside pavilion; scripted. */
export const ROAD_STAGE: StageDef = {
  id: 'memory-sword-training',
  kind: 'scripted',
  title: '路上·1157年',
  width: 640, height: 384,
  bg: 0xc9a25a, tint: 0xe2c48c, tintAlpha: 0.22,
  intro: 'prologue-stage-road-talk',
  main: 'prologue-stage-road-ship',
  player: P('jiang', 3, 8, 'right'),
  cast: [P('zhao', 4, 7, 'right')],
  interactions: [],
  sketch: [
    '####################',
    '####################',
    '####################',
    '########PPP#########',
    '########PPP#########',
    '....................',
    '....................',
    '....................',
    '....................',
    '####################',
    '####################',
    '####################',
  ],
  palette: { '#': 0xb08a4a, P: 0x8a5a32, '.': 0xd9b878 },
  vignette: { radius: 0.85, strength: 0.35, color: 0x3a2a12 },
  music: 'bgm-memory-road',
  ambience: ['amb-road-wind'],
};

export const STAGES: Record<MemoryStageId, StageDef> = {
  'memory-storm': STORM_STAGE,
  'memory-island': ISLAND_STAGE,
  'memory-black-water': BLACK_WATER_STAGE,
  'memory-farewell': FAREWELL_STAGE,
  'memory-sword-training': ROAD_STAGE,
};

export function isStageId(id: string): id is MemoryStageId {
  return Object.prototype.hasOwnProperty.call(STAGES, id);
}

/** Every timeline a stage can play (intro, main, interaction timelines and chains). */
export function stageTimelineIds(def: StageDef): string[] {
  const ids = [def.intro];
  if (def.main) ids.push(def.main);
  for (const i of def.interactions) { if (i.timeline) ids.push(i.timeline); if (i.then) ids.push(i.then); }
  return ids;
}

/** Collision grid from a sketch: '.' and 'P' are walkable. */
export function stageCollision(def: StageDef): { width: number; height: number; grid: number[] } {
  const rows = def.sketch;
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const grid: number[] = [];
  for (const r of rows) for (const ch of r) grid.push(ch === '.' || ch === 'P' ? 0 : 1);
  return { width, height, grid };
}

/** World-pixel collision query over a sketch grid (outside the grid = blocked). */
export function stageBlockedFn(def: StageDef): (px: number, py: number) => boolean {
  const col = stageCollision(def);
  return (px, py) => {
    const tx = Math.floor(px / 32), ty = Math.floor(py / 32);
    if (tx < 0 || ty < 0 || tx >= col.width || ty >= col.height) return true;
    return col.grid[ty * col.width + tx] !== 0;
  };
}
